import React, { useState } from 'react';
import { FaArrowRight, FaShieldAlt, FaCalculator, FaGlobe, FaUser, FaLock, FaFileAlt, FaCheckCircle, FaMobile, FaQrcode, FaEnvelope, FaDownload } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const TaxReportingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqData = [
    {
      question: "How do I get started with crypto tax reporting?",
      answer: "Getting started is simple! Connect your crypto accounts, let our system calculate your gains and losses, and download your tax-ready reports in minutes."
    },
    {
      question: "How much does the tax reporting service cost?",
      answer: "We offer competitive pricing with exclusive discounts for our clients. Contact us for a personalized quote based on your transaction volume and reporting needs."
    },
    {
      question: "What is automated crypto tax reporting?",
      answer: "Automated crypto tax reporting eliminates manual calculations by automatically importing your transaction data, calculating gains/losses, and generating compliant tax reports."
    },
    {
      question: "What crypto assets are supported?",
      answer: "We support all major cryptocurrencies including Bitcoin, Ethereum, and thousands of other digital assets across multiple exchanges and wallets."
    },
    {
      question: "Is my data secure?",
      answer: "Absolutely. We use bank-level encryption and security measures to protect your financial data. Your privacy and security are our top priorities."
    }
  ];

  const features = [
    {
      icon: <FaShieldAlt className="w-8 h-8 text-primary-500" />,
      title: "Stay compliant for the long run",
      description: "Benefit from a reliable, automated solution that keeps you compliant with tax regulations year after year."
    },
    {
      icon: <FaFileAlt className="w-8 h-8 text-primary-500" />,
      title: "Generate reports in minutes",
      description: "Create all the crypto tax reports you need quickly and efficiently, saving you hours of manual work."
    },
    {
      icon: <FaCheckCircle className="w-8 h-8 text-primary-500" />,
      title: "Keep your data safe and secure",
      description: "Your financial information is protected with enterprise-grade security and encryption protocols."
    }
  ];

  const complianceFeatures = [
    {
      icon: <FaCalculator className="w-8 h-8 text-primary-500" />,
      title: "Accurate calculations",
      description: "Advanced algorithms ensure precise calculation of your crypto gains, losses, and taxable events."
    },
    {
      icon: <FaGlobe className="w-8 h-8 text-primary-500" />,
      title: "Comprehensive coverage",
      description: "Support for global exchanges, wallets, and blockchain networks to capture all your crypto activity."
    },
    {
      icon: <FaUser className="w-8 h-8 text-primary-500" />,
      title: "Expert support",
      description: "Get help from our team of crypto tax professionals whenever you need assistance."
    },
    {
      icon: <FaLock className="w-8 h-8 text-primary-500" />,
      title: "Data security",
      description: "Bank-level encryption and security measures protect your sensitive financial information."
    },
    {
      icon: <FaFileAlt className="w-8 h-8 text-primary-500" />,
      title: "Tax-ready reports",
      description: "Generate reports that are immediately ready for submission to tax authorities."
    }
  ];

  const steps = [
    {
      number: "1",
      title: "Connect your crypto accounts",
      description: "Securely connect your exchange accounts, wallets, and other crypto platforms through our API integration."
    },
    {
      number: "2",
      title: "Automated calculations",
      description: "Our system automatically analyzes your transaction history and calculates your crypto gains, losses, and taxable events."
    },
    {
      number: "3",
      title: "Download your tax reports",
      description: "Generate and download comprehensive tax reports that are ready for submission to tax authorities."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container-custom py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                Save time on crypto taxes,<br />
                <span className="text-primary-400">build wealth instead</span>
              </h1>
              <p className="text-xl text-gray-300 max-w-lg">
                Manual crypto tax reporting is a thing of the past. Our automated solution helps you stay compliant while focusing on what matters most - growing your portfolio.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 group">
                  Get Started
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
                <button className="border border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold transition-all duration-300">
                  Learn More
                </button>
              </div>
              <p className="text-sm text-gray-400">Join 10,000+ crypto investors</p>
            </div>
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <span className="text-sm text-gray-300">Tax Report Ready</span>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-gray-900">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <FaCheckCircle className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="font-semibold text-lg">Tax Reporting Complete</h3>
                      <p className="text-sm text-gray-600">Your crypto tax report is ready for download</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Stay compliant effortlessly
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our automated solution handles the complexity of crypto tax reporting so you can focus on your investments.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Exclusive Discount Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold">
                Enjoy an exclusive discount on your crypto tax reports
              </h2>
              <p className="text-xl text-gray-300">
                We've partnered with leading crypto tax software providers to offer our clients special pricing and exclusive benefits on comprehensive tax reporting solutions.
              </p>
              <button className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300">
                Get 20% Off
              </button>
            </div>
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="text-center text-white">
                  <div className="w-24 h-24 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaFileAlt className="w-12 h-12" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Tax Report Ready</h3>
                  <p className="text-sm text-gray-300 mb-4">Automated for speed and simplicity</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Free transactions</span>
                      <span className="text-primary-400">1000</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Discount</span>
                      <span className="text-primary-400">20% off</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              All your crypto activity in one place
            </h2>
            <p className="text-xl text-gray-600">Here's how it works:</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <button className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 mx-auto group">
              Get Started
              <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </section>

      {/* Compliance Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Helping you stay compliant, effortlessly
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Automated generation of official statements for tax reporting, accounting, and transaction overview – all in one place.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {complianceFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Frequently asked questions
            </h2>
            <p className="text-xl text-gray-600">Everything you need to know about crypto tax reporting</p>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                  onClick={() => toggleFaq(index)}
                >
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  <FaArrowRight 
                    className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                      openFaq === index ? 'rotate-90' : ''
                    }`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter & App Download Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Newsletter */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">
                Get tax insights and news from the crypto industry
              </h2>
              <p className="text-gray-600">
                Stay updated with the latest crypto tax regulations, tips, and industry insights delivered to your inbox.
              </p>
              <div className="flex gap-3">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <button className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300 flex items-center gap-2">
                  <FaEnvelope />
                  Subscribe
                </button>
              </div>
            </div>
            
            {/* App Download */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">
                Download our app on iOS & Android
              </h2>
              <p className="text-gray-600">
                Access your tax reports and crypto portfolio management tools on the go with our mobile applications.
              </p>
              <div className="flex gap-4">
                <button className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300 flex items-center gap-2">
                  <FaDownload />
                  App Store
                </button>
                <button className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300 flex items-center gap-2">
                  <FaDownload />
                  Google Play
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to simplify your crypto tax reporting?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Join thousands of crypto investors who trust our automated tax reporting solution to stay compliant and save time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 mx-auto sm:mx-0 group">
              Get Started Today
              <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button className="border border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold transition-all duration-300">
              Schedule Demo
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TaxReportingPage; 